import React, { lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import { PrivateRoute } from "@wf/keycloak-axios-provider";
import { ErrorBoundary, WaitingHoc } from "@pl/paylican-ui-kit";

import * as pathnames from "../constants/pathnames";
import Layout from "./Modules/Layout";
import ngTheme from "./AppRouterNG.module.scss";
import AccessDeniedTranslated from "./AccessDeniedTranslated";
import {
  ENABLE_INVOICE_PROCESSING,
  APP_THEME,
  PUBLIC_PATH,
  PAYMENT_AGENT,
  ENABLE_INVOICE_CREATION
} from "../constants/envVariables";

const theme = APP_THEME === "NG" ? ngTheme : undefined;
const enableInvoiceProcessing = ENABLE_INVOICE_PROCESSING === "true";
const enableInvoiceCreation = ENABLE_INVOICE_CREATION === "true";

const InvoicePage = lazy(() => import("./Invoice"));
const CreateEditStatementPage = lazy(() => import("./CreateEditStatement"));
const InvoiceListPage = lazy(() => import("./InvoiceList"));
const InvoiceSharePage = lazy(() => import("./InvoiceShare"));
const DashboardPage = lazy(() => import("./Dashboard"));
const PrepaymentPage = lazy(() => import("./Prepayment"));
const Transactions = lazy(() => import("./Transactions"));
const InvoiceProcessingPage = lazy(() => import("./InvoiceProcessing"));
const InvoiceCreationPage = lazy(() => import("./InvoiceCreation"));

const InvoiceListPageComponent = WaitingHoc(InvoiceListPage, theme);
const InvoiceSharePageComponent = WaitingHoc(InvoiceSharePage);
const CreateEditStatementPageComponent = WaitingHoc(CreateEditStatementPage, theme);
const InvoicePageComponent = WaitingHoc(InvoicePage, theme);
const DashboardPageComponent = WaitingHoc(DashboardPage, theme);
const PrepaymentPageComponent = WaitingHoc(PrepaymentPage, theme);
const TransactionsComponent = WaitingHoc(Transactions, theme);
const InvoiceProcessingComponent = WaitingHoc(InvoiceProcessingPage, theme);
const InvoiceCreationComponent = WaitingHoc(InvoiceCreationPage, theme);

const AppRouter = () => (
  <BrowserRouter basename={PUBLIC_PATH}>
    <ErrorBoundary>
      <Routes>
        <Route element={<PrivateRoute />}>
          <Route element={<Layout />}>
            <Route
              exact
              path={pathnames.main}
              element={
                <PrivateRoute
                  roles={{
                    "invoice-system-client": [
                      "COMPANY",
                      "INDIVIDUAL",
                      "HELP_DESK",
                      "SUPER_HELP_DESK",
                      "MERCHANT",
                      "BENEFICIARY"
                    ]
                  }}
                  accessDeniedComponent={<AccessDeniedTranslated />}
                />
              }
            >
              <Route path={pathnames.main} element={<InvoiceListPageComponent />} />
            </Route>
            <Route
              exact
              path={"/payments/*"}
              element={
                <PrivateRoute
                  roles={{
                    "invoice-system-client": ["COMPANY", "INDIVIDUAL", "HELP_DESK", "SUPER_HELP_DESK"]
                  }}
                  accessDeniedComponent={<AccessDeniedTranslated />}
                />
              }
            >
              <Route exact path={"*"} element={<TransactionsComponent />} />
            </Route>
            <Route
              exact
              path={pathnames.createStatement}
              element={
                <PrivateRoute
                  roles={{ "invoice-system-client": ["COMPANY", "INDIVIDUAL"] }}
                  accessDeniedComponent={<AccessDeniedTranslated />}
                />
              }
            >
              <Route path={pathnames.createStatement} element={<CreateEditStatementPageComponent />} />
            </Route>
            <Route
              exact
              path={pathnames.editStatement}
              element={
                <PrivateRoute
                  roles={{ "invoice-system-client": ["COMPANY", "INDIVIDUAL"] }}
                  accessDeniedComponent={<AccessDeniedTranslated />}
                />
              }
            >
              <Route path={pathnames.editStatement} element={<CreateEditStatementPageComponent />} />
            </Route>
            {PAYMENT_AGENT === "true" && (
              <Route
                path={pathnames.shareInvoice}
                element={
                  <PrivateRoute
                    roles={{ "invoice-system-client": ["COMPANY", "INDIVIDUAL", "HELP_DESK", "SUPER_HELP_DESK"] }}
                    accessDeniedComponent={<AccessDeniedTranslated />}
                  />
                }
              >
                <Route path={pathnames.shareInvoice} element={<InvoiceSharePageComponent />} />
              </Route>
            )}
            <Route
              path={pathnames.invoice}
              element={
                <PrivateRoute
                  roles={{
                    "invoice-system-client": [
                      "COMPANY",
                      "INDIVIDUAL",
                      "HELP_DESK",
                      "SUPER_HELP_DESK",
                      "MERCHANT",
                      "BENEFICIARY"
                    ]
                  }}
                  accessDeniedComponent={<AccessDeniedTranslated />}
                />
              }
            >
              <Route path={pathnames.invoice} element={<InvoicePageComponent />} />
            </Route>
            {enableInvoiceCreation && (
              <Route
                path={pathnames.invoiceCreation}
                element={
                  <PrivateRoute
                    roles={{ "invoice-system-client": ["MERCHANT", "SUPER_MERCHANT"] }}
                    accessDeniedComponent={<AccessDeniedTranslated />}
                  />
                }
              >
                <Route path={pathnames.invoiceCreation} element={<InvoiceCreationComponent />} />
              </Route>
            )}
            <Route
              exact
              path={pathnames.dashboard}
              element={
                <PrivateRoute
                  roles={{
                    "invoice-system-client": ["COMPANY", "INDIVIDUAL", "HELP_DESK", "SUPER_HELP_DESK", "MERCHANT"]
                  }}
                  accessDeniedComponent={<AccessDeniedTranslated />}
                />
              }
            >
              <Route path={pathnames.dashboard} element={<DashboardPageComponent />} />
            </Route>
            <Route
              exact
              path={pathnames.prepayment}
              element={<PrivateRoute accessDeniedComponent={<AccessDeniedTranslated />} />}
            >
              <Route path={pathnames.prepayment} element={<PrepaymentPageComponent />} />
            </Route>
            {enableInvoiceProcessing && (
              <>
                <Route
                  exact
                  path={pathnames.invoiceProcessing}
                  element={
                    <PrivateRoute
                      roles={{
                        "invoice-system-client": ["SUPER_HELP_DESK"]
                      }}
                      accessDeniedComponent={<AccessDeniedTranslated />}
                    />
                  }
                >
                  <Route path={pathnames.invoiceProcessing} element={<InvoiceProcessingComponent />} />
                </Route>
                <Route
                  exact
                  path={`${pathnames.invoiceProcessing}/:id`}
                  element={
                    <PrivateRoute
                      roles={{
                        "invoice-system-client": ["SUPER_HELP_DESK"]
                      }}
                      accessDeniedComponent={<AccessDeniedTranslated />}
                    />
                  }
                >
                  <Route path={`${pathnames.invoiceProcessing}/:id`} element={<InvoiceProcessingComponent />} />
                </Route>
              </>
            )}
          </Route>
        </Route>
      </Routes>
    </ErrorBoundary>
  </BrowserRouter>
);

export default AppRouter;
